import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ShareButtonProps {
  title: string;
  text: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({ title, text }) => {
  const [showToast, setShowToast] = useState(false);
  const shareUrl = 'https://moneywrapped2024.com';
  
  const showCopiedToast = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const handleShare = async () => {
    const shareData = {
      title,
      text: `${text}\n\n${shareUrl}`,
      url: shareUrl
    };

    try {
      // First try native sharing (works best on mobile)
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      // If native sharing fails or isn't available, try clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(`${title}\n${text}\n\n${shareUrl}`);
        showCopiedToast();
        return;
      }

      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = `${title}\n${text}\n\n${shareUrl}`;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        textArea.remove();
        showCopiedToast();
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
        textArea.remove();
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // If all sharing methods fail, open a new window
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData.text)}`,
        '_blank'
      );
    }
  };

  return (
    <div className="flex flex-col items-center relative">
      <motion.button
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleShare}
        className="group relative mt-8 px-8 py-4 bg-[#9B86EA] text-white rounded-xl font-bold text-lg
                   flex items-center justify-center gap-3 transition-all duration-200
                   border-4 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51]
                   hover:shadow-[6px_6px_0px_0px_#221A51] hover:translate-x-[-2px] hover:translate-y-[-2px]
                   active:translate-x-[2px] active:translate-y-[2px] active:shadow-[2px_2px_0px_0px_#221A51]"
      >
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 20 20" 
          fill="none" 
          className="group-hover:scale-110 transition-transform duration-200"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M10 3v10M10 3l4 4M10 3L6 7" 
            stroke="currentColor" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="group-hover:stroke-[3]"
          />
        </svg>
        <span className="relative">
          Share this story
          <span className="absolute inset-x-0 -bottom-1 border-b-2 border-white/0 group-hover:border-white/50 transition-all duration-200"></span>
        </span>
      </motion.button>

      <div className="mt-4 text-[#221A51] text-lg sm:text-xl font-bold tracking-tight select-all">
        moneywrapped2024.com
      </div>

      {/* Toast Notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 
                     bg-[#221A51] text-white px-4 py-2 rounded-lg
                     shadow-lg text-sm font-medium z-50"
          >
            Copied to clipboard!
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShareButton;
